import React, { useState } from "react";
import { Shield } from "lucide-react";

export default function AuthCard({ isMobile, onMosipSignIn, onSupport, isLoading }) {
  const [localLoading, setLocalLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [language, setLanguage] = useState("English");

  const handleESignetAuth = async () => {
    try {
      setLocalLoading(true);
      setAuthError(null);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/initiate`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data.success && data.authUrl) {
        window.location.href = data.authUrl; // redirect to eSignet
      } else {
        setAuthError("Failed to initiate login: " + (data.error || "Unknown error"));
        setLocalLoading(false);
      }
    } catch (err) {
      console.error("Login initiation error:", err);
      setAuthError("Failed to start login. Please try again.");
      setLocalLoading(false);
    }
  };

  const currentLoading = isLoading || localLoading;

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '16px' : 'clamp(20px, 5vw, 40px)',
      // backgroundImage: 'url(/assets/Images/SLUDI-background.png)',
      // backgroundSize: 'cover',
      // backgroundPosition: 'center',
      // backgroundRepeat: 'no-repeat',
    },

    cardWrapper: {
      width: '100%',
      maxWidth: '720px',
      position: 'relative',
    },

    card: {
      width: "100%",
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      overflow: "hidden",
      fontFamily: "'Barlow', sans-serif",
    },

    header: {
      padding: isMobile ? "24px 16px 0" : "clamp(28px, 5vw, 40px) clamp(20px, 6vw, 48px) 0",
    },

    logoRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "20px",
      gap: "16px",
      flexWrap: isMobile ? "wrap" : "nowrap",
    },

    leftSection: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      flex: 1,
      minWidth: 0,
    },

    logo: {
      height: isMobile ? "44px" : "clamp(48px, 6vw, 60px)",
      width: "auto",
      flexShrink: 0,
    },

    headerTitle: {
      fontSize: isMobile ? "16px" : "clamp(18px, 2.6vw, 22px)",
      fontWeight: "700",
      color: "#000000",
      margin: "0",
      fontFamily: "'Barlow', sans-serif",
      lineHeight: "1.2",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      textAlign: isMobile ? "left" : "center",
      wordBreak: "break-word",
    },

    rightLogo: {
      height: isMobile ? "42px" : "clamp(46px, 6vw, 60px)",
      width: "auto",
      flexShrink: 0,
    },

    welcomeText: {
      fontSize: isMobile ? "13px" : "clamp(14px, 2vw, 15px)",
      color: "#4b5563",
      margin: "0 0 24px 0",
      lineHeight: 1.5,
      fontFamily: "'Barlow', sans-serif",
      fontWeight: "400",
      textAlign: "center",
    },

    divider: {
      height: "1px",
      backgroundColor: "#e5e7eb",
      margin: "0",
    },

    body: {
      padding: isMobile ? "24px 16px" : "clamp(28px, 5vw, 40px) clamp(20px, 6vw, 48px)",
    },

    languageSection: {
      marginBottom: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      flexWrap: "wrap",
    },

    languageLabel: {
      fontSize: "15px",
      color: "#374151",
      fontWeight: "500",
      fontFamily: "'Barlow', sans-serif",
    },

    languageSelect: {
      padding: "5px 40px 5px 12px",
      fontSize: "13px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      outline: "none",
      fontFamily: "'Barlow', sans-serif",
      fontWeight: "400",
      minWidth: isMobile ? "100%" : "120px",
      width: isMobile ? "100%" : "auto",
      maxWidth: isMobile ? "280px" : "none",
      color: "#000000",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
    },

    buttonContainer: {
      marginBottom: "32px",
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },

    sludiButton: {
      padding: isMobile ? "12px 16px" : "14px 36px",
      backgroundColor: "#ffffff",
      border: "1.5px solid #d1d5db",
      borderRadius: "8px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flexWrap: isMobile ? "wrap" : "nowrap",
      cursor: currentLoading ? "not-allowed" : "pointer",
      fontSize: isMobile ? "15px" : "16px",
      fontWeight: "600",
      color: "#000000",
      transition: "all 0.2s ease",
      opacity: currentLoading ? 0.6 : 1,
      fontFamily: "'Barlow', sans-serif",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      width: isMobile ? "100%" : "auto",
      maxWidth: isMobile ? "320px" : "none",
    },

    sludiLogo: {
      height: "20px",
      width: "auto",
    },

    divider2: {
      height: "1px",
      backgroundColor: "#e5e7eb",
      margin: "0 0 32px 0",
    },

    firstTimeSection: {
      textAlign: "center",
      marginBottom: "32px",
    },

    firstTimeTitle: {
      fontSize: "16px",
      fontWeight: "700",
      color: "#000000",
      margin: "0 0 10px 0",
      fontFamily: "'Barlow', sans-serif",
    },

    firstTimeText: {
      fontSize: "15px",
      color: "#4b5563",
      margin: 0,
      fontFamily: "'Barlow', sans-serif",
      fontWeight: "400",
    },

    link: {
      color: "#2563eb",
      textDecoration: "underline",
      fontWeight: "500",
      fontFamily: "'Barlow', sans-serif",
    },

    footer: {
      padding: isMobile ? "0 16px 24px" : "0 clamp(20px, 6vw, 48px) clamp(28px, 5vw, 40px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      flexWrap: "wrap",
      fontSize: "13px",
      color: "#6b7280",
      fontFamily: "'Barlow', sans-serif",
      fontWeight: "400",
      lineHeight: "1.5",
      textAlign: "center",
    },

    shieldIcon: {
      flexShrink: 0,
    },

    errorSection: {
      backgroundColor: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "20px",
      textAlign: "center",
    },

    errorText: {
      fontSize: "13px",
      color: "#dc2626",
      margin: 0,
      fontWeight: "500",
      fontFamily: "'Barlow', sans-serif",
    },

    spinner: {
      width: "16px",
      height: "16px",
      border: "2px solid #e5e7eb",
      borderTop: "2px solid #000000",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap');
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div style={styles.container}>
        <div style={styles.cardWrapper}>
          <div style={styles.card}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.logoRow}>
                <div style={styles.leftSection}>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/Images/sri-lanka-emblem.png`}
                    alt="Sri Lanka Emblem"
                    style={styles.logo}
                  />
                  <h1 style={styles.headerTitle}>Department of Immigration and Emigration</h1>
                </div>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/Images/75th-logo.png`}
                  alt="75th Anniversary"
                  style={styles.rightLogo}
                />
              </div>

              <p style={styles.welcomeText}>
                Welcome to the portal of Department of Immigration and Emigration!
              </p>

              <div style={styles.divider} />
            </div>

            {/* Body */}
            <div style={styles.body}>
              {authError && (
                <div style={styles.errorSection} role="alert">
                  <p style={styles.errorText}>{authError}</p>
                </div>
              )}

              {/* Language Selector */}
              <div style={styles.languageSection}>
                <label style={styles.languageLabel}>Select Language</label>
                <select
                  style={styles.languageSelect}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option value="English">ENGLISH</option>
                  <option value="Sinhala">සිංහල</option>
                  <option value="Tamil">தமிழ்</option>
                </select>
              </div>

              {/* Sign in Button */}
              <div style={styles.buttonContainer}>
                <button
                  onClick={handleESignetAuth}
                  style={styles.sludiButton}
                  disabled={currentLoading}
                  onMouseEnter={(e) => {
                    if (!currentLoading) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                      e.currentTarget.style.borderColor = "#9ca3af";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!currentLoading) {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                      e.currentTarget.style.borderColor = "#d1d5db";
                    }
                  }}
                >
                  {currentLoading ? (
                    <div style={styles.spinner}></div>
                  ) : (
                    <>
                      <span>Sign in with</span>
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/Images/sludi-logo.png`}
                        alt="SLUDI"
                        style={styles.sludiLogo}
                      />
                    </>
                  )}
                </button>
              </div>

              <div style={styles.divider2} />

              {/* First Time User */}
              <div style={styles.firstTimeSection}>
                <h3 style={styles.firstTimeTitle}>First time user?</h3>
                <p style={styles.firstTimeText}>
                  Click <a href="#" style={styles.link}>here</a> to obtain verified SLUDI digital Identity.
                </p>
              </div>

              {/* Footer */}
              <div style={styles.footer}>
                <Shield size={16} style={styles.shieldIcon} color="#6b7280" />
                <span>Trusted Authentication. Verified through Sri Lankan digital identity system.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}